import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, MessageCircleIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { toast } from '../../components/ui/Toast';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, assignLead, deleteLead, deleteNote, updateLeadStage, updateNote } from '../../services/leadService';
import { answerToText, formatDateTime, fullName } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { Avatar } from '../../components/ui/Avatar';
export function LeadDetail() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    leads,
    stages,
    notes,
    history,
    forms,
    landings
  } = useWorkspace();
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const lead = leads.find(l => l.id === id);
  if (!lead) {
    return <div className="rounded-lg border border-border bg-background p-10 text-center" data-id="element-1314" data-mp="33muvjem8h17j">
        <p className="text-sm text-muted-foreground" data-id="element-1315" data-mp="2yo0d4ds6heny">Este lead ya no existe.</p>
        <Link to="/app/leads" className="mt-4 inline-block" data-id="element-1316" data-mp="1p3iymwok52">
          <Button variant="secondary" data-id="element-1317" data-mp="3reienad977z9">Volver a leads</Button>
        </Link>
      </div>;
  }
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const leadNotes = notes.filter(n => n.leadId === lead.id);
  const leadHistory = history.filter(h => h.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const stage = stages.find(s => s.id === lead.stageId);
  const canEdit = hasPermission('leads.edit');
  const details: {
    label: string;
    value: string;
  }[] = [{
    label: 'Nombre',
    value: lead.firstName
  }, {
    label: 'Apellidos',
    value: lead.lastName
  }, {
    label: 'Empresa',
    value: lead.company
  }, {
    label: 'Email',
    value: lead.email
  }, {
    label: 'Teléfono',
    value: lead.phone
  }, {
    label: 'Página web',
    value: lead.website || '—'
  }, {
    label: 'Fecha de entrada',
    value: formatDateTime(lead.createdAt)
  }, {
    label: 'Fuente',
    value: lead.origin.source
  }, {
    label: 'Campaña',
    value: lead.origin.campaign || '—'
  }, {
    label: 'Landing',
    value: landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
  }, {
    label: 'Formulario',
    value: forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
  }, {
    label: 'Último contacto',
    value: lead.lastContactAt ? formatDateTime(lead.lastContactAt) : 'Sin contactar'
  }];
  async function handleAddNote() {
    if (!noteBody.trim() || !lead) return;
    setSavingNote(true);
    await addNote(lead.organizationId, lead.id, user?.id ?? '', authorName, noteBody.trim());
    setNoteBody('');
    setSavingNote(false);
  }
  async function handleDelete() {
    if (!lead) return;
    setDeleting(true);
    try {
      await deleteLead(lead.id, authorName);
      toast.success('Lead movido a la papelera');
      navigate('/app/leads');
    } catch (error) {
      toast.error('No se ha podido mover a la papelera', {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setDeleting(false);
    }
  }
  return <>
      <Link to="/app/leads" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1318" data-mp="212c5g93olnp7">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1319" data-mp="260nfokxw207y" />
        Volver a leads
      </Link>

      <PageHeader title={fullName(lead.firstName, lead.lastName)} description={lead.company} actions={<>
            <Button variant="secondary" leftIcon={<MessageCircleIcon className="h-4 w-4" data-id="element-1322" data-mp="2f00pjepxcqt7" />} onClick={() => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank', 'noopener')} data-id="element-1321" data-mp="2f120lj6xxdax">
              WhatsApp
            </Button>
            <Button variant="secondary" leftIcon={<MailIcon className="h-4 w-4" data-id="element-1324" data-mp="1rc0p1n2t9m1f" />} onClick={() => {
        window.location.href = `mailto:${lead.email}`;
      }} data-id="element-1323" data-mp="1mbd33ftfvve2">
              Email
            </Button>
            <Button variant="secondary" leftIcon={<PhoneIcon className="h-4 w-4" data-id="element-1326" data-mp="yon1ugox04ui" />} onClick={() => {
        window.location.href = `tel:${lead.phone.replace(/\s/g, '')}`;
      }} data-id="element-1325" data-mp="1w53ogbu9rppj">
              Llamar
            </Button>
            {hasPermission('leads.delete') && <Button variant="destructive" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-1328" data-mp="w12tkv82tv3v" />} onClick={() => setConfirmDelete(true)} data-id="element-1327" data-mp="w248jas7r1qv">
                Eliminar
              </Button>}
          </>} data-id="element-1320" data-mp="2a2s85gcloqt0" />

      <div className="grid gap-5 lg:grid-cols-3" data-id="element-1329" data-mp="m47zqpmr0ivx">
        {/* Columna principal */}
        <div className="space-y-5 lg:col-span-2" data-id="element-1330" data-mp="i4fm3yihzmjp">
          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1331" data-mp="i5qn5qkk6g74">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1332" data-mp="d5w0y0bqaese">
              Datos principales
            </h2>
            <dl className="grid gap-4 sm:grid-cols-2" data-id="element-1333" data-mp="3erj8suo4ftv">
              {details.map(item => <div key={item.label} data-id="element-1334" data-mp="3f1dwtqnrb5w">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-1335" data-mp="3ulce6nnug7w6">
                    {item.label}
                  </dt>
                  <dd className="mt-1 break-words text-sm text-foreground" data-id="element-1336" data-mp="3qiy00gv4dwb8">
                    {item.value}
                  </dd>
                </div>)}
            </dl>
          </section>

          <Tabs defaultValue="respuestas" data-id="element-1337" data-mp="3bvzawsjqjdzo">
            <TabsList data-id="element-1338" data-mp="3fydsl9bo47pd">
              <TabsTrigger value="respuestas" data-id="element-1339" data-mp="3fyvmhzauy9u4">Respuestas</TabsTrigger>
              <TabsTrigger value="notas" data-id="element-1340" data-mp="3fw138b3kyv2a">Notas</TabsTrigger>
              <TabsTrigger value="historial" data-id="element-1341" data-mp="31zw6fg1ev39k">Historial</TabsTrigger>
            </TabsList>

            <TabsContent value="respuestas" data-id="element-1342" data-mp="36yqscquod7xw">
              {lead.answers.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1343" data-mp="3umqgrk4ikm9b">
                  Este lead no tiene respuestas asociadas.
                </p> : <dl className="divide-y divide-border rounded-lg border border-border bg-background" data-id="element-1344" data-mp="3kxyspn2kqm3o">
                  {lead.answers.map(answer => <div key={answer.questionId} className="p-4" data-id="element-1345" data-mp="3pv8mtg0wrlxu">
                      <dt className="text-sm font-medium text-foreground" data-id="element-1346" data-mp="hg86fjb161py">
                        {answer.label}
                      </dt>
                      <dd className="mt-1 text-sm text-muted-foreground" data-id="element-1347" data-mp="m91f2i9mdu7b">
                        {answerToText(answer.value) || '—'}
                      </dd>
                    </div>)}
                </dl>}
            </TabsContent>

            <TabsContent value="notas" data-id="element-1348" data-mp="2fq5z4dfhawsd">
              {hasPermission('notes.edit') && <div className="mb-4 rounded-lg border border-border bg-background p-4" data-id="element-1349" data-mp="2fpe0suuhykye">
                  <Textarea id="nueva-nota" label="Nueva nota" rows={3} placeholder="Escribe el resultado de la llamada, próximos pasos…" value={noteBody} onChange={e => setNoteBody(e.target.value)} data-id="element-1350" data-mp="2bp3wdmmcs2ln" />
                  <div className="mt-3 flex justify-end" data-id="element-1351" data-mp="1j1hjtov5dnmn">
                    <Button size="sm" loading={savingNote} disabled={!noteBody.trim()} onClick={handleAddNote} data-id="element-1352" data-mp="1x07hp6psjrc2">
                      Añadir nota
                    </Button>
                  </div>
                </div>}
              {leadNotes.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1353" data-mp="38d05k6fgnvmu">
                  Todavía no hay notas para este lead.
                </p> : <ul className="space-y-3" data-id="element-1354" data-mp="3njflo0tdvtjd">
                  {leadNotes.map(note => <li key={note.id} className="rounded-lg border border-border bg-background p-4" data-id="element-1355" data-mp="3sg6610c81bal">
                      <div className="flex items-center gap-3" data-id="element-1356" data-mp="c3ve5wsdjre">
                        <Avatar name={note.authorName} size="sm" data-id="element-1357" data-mp="53vclpu1yd4v" />
                        <div className="min-w-0 flex-1" data-id="element-1358" data-mp="556grqretmwi">
                          <p className="text-sm font-medium text-foreground" data-id="element-1359" data-mp="a3rgzq6nw6ky">
                            {note.authorName}
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-1360" data-mp="f45nh3sm78gv">
                            {formatDateTime(note.createdAt)}
                          </p>
                        </div>
                      </div>
                      {editingId === note.id ? <div className="mt-3" data-id="element-1361" data-mp="2ltal2p31x6i6">
                          <Textarea id={`edit-${note.id}`} rows={3} value={editingBody} onChange={e => setEditingBody(e.target.value)} data-id="element-1362" data-mp="2lulouowhye9a" />
                          <div className="mt-2 flex justify-end gap-2" data-id="element-1363" data-mp="2ib4qdf0uwxsr">
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-id="element-1364" data-mp="2icfvbi61gblg">
                              Cancelar
                            </Button>
                            <Button size="sm" onClick={async () => {
                      await updateNote(note.id, editingBody);
                      setEditingId(null);
                    }} data-id="element-1365" data-mp="28lbhwmw0gysq">
                              Guardar
                            </Button>
                          </div>
                        </div> : <>
                          <p className="mt-3 text-sm leading-relaxed text-foreground" data-id="element-1366" data-mp="2s8raqh0xdhk4">
                            {note.body}
                          </p>
                          {note.authorId === user?.id && hasPermission('notes.edit') && <div className="mt-3 flex gap-2" data-id="element-1367" data-mp="1gu5d3t5cicx4">
                              <Button size="sm" variant="ghost" onClick={() => {
                      setEditingId(note.id);
                      setEditingBody(note.body);
                    }} data-id="element-1368" data-mp="1gr9d7z9xte61">
                                Editar
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteNote(note.id)} data-id="element-1369" data-mp="2iovbnjv7sttw">
                                Eliminar
                              </Button>
                            </div>}
                        </>}
                    </li>)}
                </ul>}
            </TabsContent>

            <TabsContent value="historial" data-id="element-1370" data-mp="36as4ldl4tmt7">
              <ol className="space-y-3" data-id="element-1371" data-mp="31e1o4p5f0p79">
                {leadHistory.map(event => <li key={event.id} className="flex gap-3 rounded-lg border border-border bg-background p-4" data-id="element-1372" data-mp="31fw73xx4m78p">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-primary" data-id="element-1373" data-mp="3bbps85owv923" />
                    <div data-id="element-1374" data-mp="3b9v9my143t16">
                      <p className="text-sm text-foreground" data-id="element-1375" data-mp="1kyqmrl0lk3w2">{event.description}</p>
                      <p className="mt-1 text-xs text-muted-foreground" data-id="element-1376" data-mp="1gxybkm85zox5">
                        {event.authorName} · {formatDateTime(event.createdAt)}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </TabsContent>
          </Tabs>
        </div>

        {/* Columna lateral */}
        <aside className="space-y-5" data-id="element-1377" data-mp="x2vnwkt7mll7">
          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1378" data-mp="x1uc2e4ddv2f">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1379" data-mp="1216zazz5l45j">Estado</h2>
            <div className="mb-4" data-id="element-1380" data-mp="n4zb7okmjikz">
              <StatusBadge status={(stage?.name ?? 'Nuevo') as never} data-id="element-1381" data-mp="n83odbg2hons" />
            </div>
            <Select id="estado" label="Cambiar estado" disabled={!canEdit} options={stages.map(s => ({
            value: s.id,
            label: s.name
          }))} value={lead.stageId} onChange={e => updateLeadStage(lead.id, e.target.value, authorName)} data-id="element-1382" data-mp="s2r68rgkfkte" />
            <div className="mt-4" data-id="element-1383" data-mp="28k56z68ffblz">
              <Select id="comercial" label="Comercial asignado" placeholder="Sin asignar" disabled={!canEdit} options={members.map(m => ({
              value: m.userId,
              label: fullName(m.firstName, m.lastName)
            }))} value={lead.assignedTo ?? ''} onChange={e => {
              const member = members.find(m => m.userId === e.target.value);
              assignLead(lead.id, e.target.value || undefined, member ? fullName(member.firstName, member.lastName) : '', authorName);
            }} data-id="element-1384" data-mp="28h0s9exc4xh2" />
            </div>
          </section>

          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1385" data-mp="2uqhcb1plxaw9">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1386" data-mp="2ps640veyn8g5">
              Origen del lead
            </h2>
            <dl className="space-y-3 text-sm" data-id="element-1387" data-mp="2l0ekh4u822zi">
              {[['source', lead.origin.source], ['medium', lead.origin.medium], ['campaign', lead.origin.campaign || '—'], ['content', lead.origin.content || '—'], ['term', lead.origin.term || '—'], ['referrer', lead.origin.referrer || 'directo']].map(([label, value]) => <div key={label} className="flex justify-between gap-3" data-id="element-1388" data-mp="38ppf04kzb157">
                  <dt className="text-muted-foreground" data-id="element-1389" data-mp="3g387yni6p0f1">{label}</dt>
                  <dd className="truncate text-foreground" data-id="element-1390" data-mp="3l1jgmuwqt4vo">{value}</dd>
                </div>)}
            </dl>
          </section>
        </aside>
      </div>

      <ConfirmDialog open={confirmDelete} title="Eliminar este lead" description="Se eliminarán también sus notas y su historial. Esta acción no se puede deshacer." loading={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={handleDelete} data-id="element-1391" data-mp="3pz1enq981zow" />
    </>;
}